import type { ParticipantEditFormValues } from '../../../domain/types'
import { Typography } from '@sunwebgroup/uds-ui/typography'
import cn from 'classnames'
import { FormProvider, useForm } from 'react-hook-form'
import { useIntl } from '@/intl'
import { useBookingDetailsActions } from '@/scopes/account/booking-details/infrastructure/dependencies/dependencies'
import { getDefaultPhoneCountry } from '@/scopes/common/domain/policies/defaultPhoneCountry'
import { useConfiguration } from '@/scopes/common/infrastructure/config/ConfigProvider'
import { useBookingDetailsStore } from '../../../infrastructure/useBookingDetailsStore'
import { getParticipantEditFields, getParticipantEditFormValues } from '../../selectors'
import { ParticipantEditNoChangesBanner } from './ParticipantEditNoChangesBanner'
import { ParticipantEditSaveErrorBanner } from './ParticipantEditSaveErrorBanner'
import { ParticipantEditSavingMessage } from './ParticipantEditSavingMessage'
import { ParticipantEditSections } from './ParticipantEditSections'
import styles from './ParticipantEditShared.module.css'

export const PARTICIPANT_EDIT_EXTERNAL_REQUEST_FORM_ID = 'participant-edit-external-request-form'

export function ParticipantEditExternalRequestForm() {
  const { formatMessage } = useIntl()
  const { participantEditExternalRequestSubmitted, participantEditExternalRequestUnchangedSubmitted } = useBookingDetailsActions()
  const locale = useConfiguration('locale')
  const fields = useBookingDetailsStore(state => getParticipantEditFields(state, 'externalRequest'))
  const values = useBookingDetailsStore(state => getParticipantEditFormValues(state, 'externalRequest', getDefaultPhoneCountry(locale)))
  const methods = useForm<ParticipantEditFormValues>({ values })
  const { isDirty } = methods.formState
  const wasSubmittedUnchanged = useBookingDetailsStore(state => state.participantEdit.externalRequest.wasSubmittedUnchanged)
  const submitStatus = useBookingDetailsStore(state => state.participantEdit.externalRequest.submitStatus)
  const isSubmitting = submitStatus === 'loading'

  function handleSubmit(values: ParticipantEditFormValues) {
    return isDirty ? participantEditExternalRequestSubmitted(values) : participantEditExternalRequestUnchangedSubmitted()
  }

  return (
    <FormProvider {...methods}>
      {isSubmitting && <ParticipantEditSavingMessage />}
      <form
        id={PARTICIPANT_EDIT_EXTERNAL_REQUEST_FORM_ID}
        className={cn(styles.form, isSubmitting && styles.formHidden)}
        onSubmit={methods.handleSubmit(handleSubmit)}
        noValidate
      >
        {submitStatus === 'error' && <ParticipantEditSaveErrorBanner />}
        {wasSubmittedUnchanged && !isDirty && <ParticipantEditNoChangesBanner />}
        <Typography as="p" variant="body-m-regular-snug">
          {formatMessage({ id: 'booking_details_participant_edit_external_request_intro' })}
        </Typography>
        <ParticipantEditSections fields={fields} />
      </form>
    </FormProvider>
  )
}
